import React from 'react'
import {observer} from 'mobx-react-lite'
import {ColDef, GridApi, GridOptions} from 'ag-grid-community'
import {Grid, Typography, Tooltip, IconButton} from '@mui/material'
import {Forward} from '@mui/icons-material'
import {makeStyles} from '@oc/styles'
import {debounce} from 'lodash'
import {SearchIcon} from '@oc/icons'
import {OcTransferListGrid, OcTransferListGridProps} from './TransferListGrid'
import {useColumnDefs, useGridOptions, OcTransferDirection, OcTransferListTranslations} from './transferListConfig'
import {messages} from './translations'
import './transferListStyles.css'
import {OcTextField} from '../BasicOcComponents/OcTextFields'

const useStyles = makeStyles({
    fullHeight: {
        height: '100%'
    },
    flexGrow: {
        flexGrow: 1
    },
    rotateLeft: {
        transform: 'rotate(180deg)'
    },
    controlsGrid: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '56px'
    }
})

export interface OcTransferListProps<T> {
    left: OcTransferListGridProps<T>
    right: OcTransferListGridProps<T>
    columnDefs?: ColDef<T>[]
    gridOptions?: GridOptions<T>
    transferWithRowClick?: boolean
    transferWithRowButtons?: boolean
    transferWithSharedButtons?: boolean
    orderableWithRowButtons?: boolean
    orderableWithDragAndDrop?: boolean
    translations?: OcTransferListTranslations
    disabled?: boolean
}

function _OcTransferList<T>(props: OcTransferListProps<T>) {
    const {
        left,
        right,
        transferWithRowButtons,
        transferWithRowClick,
        transferWithSharedButtons,
        translations,
        disabled
    } = props
    const {classes, cx} = useStyles()

    const [leftSelectedRows, setLeftSelectedRows] = React.useState<T[]>([])
    const [rightSelectedRows, setRightSelectedRows] = React.useState<T[]>([])

    const [leftGridApi, setLeftGridApi] = React.useState<GridApi | undefined>(undefined)
    const [rightGridApi, setRightGridApi] = React.useState<GridApi | undefined>(undefined)

    const leftColumnDefs = useColumnDefs(props, left, OcTransferDirection.Right)
    const rightColumnDefs = useColumnDefs(props, right, OcTransferDirection.Left)

    const leftGridOptions = useGridOptions(props, left, setLeftGridApi)
    const rightGridOptions = useGridOptions(props, right, setRightGridApi)

    const [leftFilterValue, setLeftFilterValue] = React.useState('')
    const [rightFilterValue, setRightFilterValue] = React.useState('')

    const onLeftFilterChange = React.useCallback(
        (newValue: string) => {
            leftGridApi?.setGridOption('quickFilterText', newValue)
        },
        [leftGridApi]
    )

    const onRightFilterChange = React.useCallback(
        (newValue: string) => {
            rightGridApi?.setGridOption('quickFilterText', newValue)
        },
        [rightGridApi]
    )

    const debounceLeftOnChange = React.useMemo(
        () => debounce(onLeftFilterChange, 500, {leading: true}),
        [onLeftFilterChange]
    )

    const debounceRightOnChange = React.useMemo(
        () => debounce(onRightFilterChange, 500, {leading: true}),
        [onRightFilterChange]
    )

    const transferRight = React.useCallback(() => {
        for (const selectedRow of leftSelectedRows) {
            left.transfer(selectedRow)
        }
    }, [leftSelectedRows, left])

    const transferLeft = React.useCallback(() => {
        for (const selectedRow of rightSelectedRows) {
            right.transfer(selectedRow)
        }
    }, [rightSelectedRows, right])

    if (transferWithSharedButtons && transferWithRowClick) {
        throw new Error("Both transferWithSharedButtons and transferWithRowClick can't be enabled at same time")
    }

    if (!transferWithRowButtons && !transferWithRowClick && !transferWithSharedButtons && !disabled) {
        throw new Error('At least one transfer method must be selected')
    }

    const reserveSpaceForTitles = left.title || right.title || left.quickSearch || right.quickSearch

    return (
        <Grid container spacing={1} data-cy="transfer-list" className={cx(classes.fullHeight, 'transfer-list')}>
            <Grid item className={classes.flexGrow}>
                <Grid container direction="column" spacing={1} className={classes.fullHeight}>
                    {reserveSpaceForTitles && (
                        <Grid item className={classes.controlsGrid}>
                            <Typography variant={left.titleVariant ? left.titleVariant : 'h6'}>
                                {left.title || '\xA0'}
                            </Typography>
                            {left.quickSearch ? (
                                <OcTextField
                                    variant="outlined"
                                    value={leftFilterValue}
                                    data-cy="transferlist-left-quickfilter"
                                    onChange={e => {
                                        setLeftFilterValue(e.target.value)
                                        debounceLeftOnChange(e.target.value)
                                    }}
                                    label={'Search'}
                                    startIcon={<SearchIcon size="24px" />}
                                />
                            ) : undefined}
                        </Grid>
                    )}
                    <Grid item className={classes.flexGrow} data-cy="transfer-list-left">
                        <OcTransferListGrid<T>
                            {...left}
                            gridOptions={leftGridOptions}
                            columnDefs={leftColumnDefs}
                            setSelectedRows={setLeftSelectedRows}
                        />
                    </Grid>
                </Grid>
            </Grid>
            {!disabled && transferWithSharedButtons ? (
                <Grid item>
                    <Grid
                        container
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                        className={classes.fullHeight}
                    >
                        <Tooltip title={translations?.transferRight || messages.getText('buttons', 'transferRight')}>
                            <span>
                                <IconButton
                                    onClick={transferRight}
                                    data-cy="transfer-list-shared-buttons-right"
                                    disabled={leftSelectedRows.length === 0}
                                    size="large"
                                >
                                    <Forward />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={translations?.transferLeft || messages.getText('buttons', 'transferLeft')}>
                            <span>
                                <IconButton
                                    onClick={transferLeft}
                                    data-cy="transfer-list-shared-buttons-left"
                                    disabled={rightSelectedRows.length === 0}
                                    size="large"
                                >
                                    <Forward className={classes.rotateLeft} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
            ) : undefined}
            <Grid item className={classes.flexGrow}>
                <Grid container direction="column" spacing={1} className={classes.fullHeight}>
                    {reserveSpaceForTitles && (
                        <Grid item className={classes.controlsGrid}>
                            <Typography variant={right.titleVariant ? right.titleVariant : 'h6'}>
                                {right.title || '\xA0'}
                            </Typography>
                            {right.quickSearch ? (
                                <OcTextField
                                    variant="outlined"
                                    value={rightFilterValue}
                                    data-cy="transferlist-right-quickfilter"
                                    onChange={e => {
                                        setRightFilterValue(e.target.value)
                                        debounceRightOnChange(e.target.value)
                                    }}
                                    label={'Search'}
                                    startIcon={<SearchIcon size="24px" />}
                                />
                            ) : undefined}
                        </Grid>
                    )}
                    <Grid item className={classes.flexGrow} data-cy="transfer-list-right">
                        <OcTransferListGrid<T>
                            {...right}
                            gridOptions={rightGridOptions}
                            columnDefs={rightColumnDefs}
                            setSelectedRows={setRightSelectedRows}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}
export const OcTransferList = observer(_OcTransferList)
